<template>
    <v-card>
        <invoice-filters
            :invoiceTypes="types"
            :invoiceType="localType"
            @update:invoiceType="updateType"
            @update:search="updateSearch"
            @update:sort="updateSort"
        />
        <v-data-table
            item-key="id"
            :headers="invoiceHeaders"
            :items="invoices"
            :loading="loading.invoices"
            :options.sync="options"
            :server-items-length="totalInvoices"
            single-expand
            show-expand
            sort-by="id"
            class="elevation-1"
        >
            <template v-slot:item.invoice_issued_at="{ item }">
                {{ item.invoice_issued_at == null ? item.invoice_issued_at : formatDatetime(item.invoice_issued_at) }}
            </template>
            <template v-slot:item.subtotal_cost="{ item }">
                {{ getFormattedField(item.subtotal_cost) }}
            </template>
            <template v-slot:item.total_cost="{ item }">
                {{ getFormattedField(item.total_cost) }}
            </template>
            <template v-slot:item.actions="{ item }">
                <dynamic-speed-dial
                    :item="item"
                    editItemIcon="mdi-invoice-edit"
                    @download:item-report="downloadInvoiceReport"
                    @edit:item="editItem"
                    @delete:item="deleteItemConfirmDialog"
                    @view:item="viewItem"
                />
            </template>
            <template v-slot:expanded-item="{ headers, item }">
                <td :colspan="headers.length" class="indigo lighten-1">
                    <invoice-details
                        :invoice="item"
                        :invoiceHeaders="headers"
                        :expenseHeaders="expenseHeaders"
                    />
                </td>
            </template>
        </v-data-table>
    </v-card>
</template>

<script>
/**
 * @name InvoiceTable
 * @description
 * This component is used to display a table of invoices
 * @param {Array} types - The types of invoices
 * @param {Object} type - The type of invoice
 * @param {Array} invoiceHeaders - The headers for the invoice table
 * @param {Array} expenseHeaders - The headers for the expense table
 * @emits update:type - Emits the update:type event with the new type when the type is updated
 *
 * @example
 *  <invoice-table
 *      :types="invoiceTypes"
 *      :type="invoiceType"
 *      :invoiceHeaders="invoiceHeaders"
 *      :expenseHeaders="expenseHeaders"
 *      @update:type="invoiceType = $event"
 *  />
 */
import _ from 'lodash';
import axios from 'axios';
import { sheets } from '@/Utils/dataSheets';

import InvoiceFilters from '../../components/Maintenance/InvoiceFilters.vue';
import DynamicSpeedDial from '@/components/Global/Utils/DynamicSpeedDial.vue';
import InvoiceDetails from '@/components/Maintenance/InvoiceDetails.vue';

export default {
    name: 'InvoiceTable',
    components: {
        InvoiceFilters,
        DynamicSpeedDial,
        InvoiceDetails
    },
    props: {
        types: Array,
        type: Object,
        invoiceHeaders: Array,
        expenseHeaders: Array
    },
    data() {
        return {
            localType: this.type,
            invoices: [],
            loading: {
                invoices: false
            },

            //Table parameters
            search: '',
            options: {
                page: 1,
                itemsPerPage: 10,
                sortBy: ['id'],
                sortDesc: [true],
                search: '',
                filterBy: ''
            },
            totalInvoices: 0
        };
    },
    computed: {
        authentication() {
            return {
                headers: {
                    Authorization: 'Bearer ' + this.$store.state.token
                }
            };
        }
    },
    created() {
        // Create a debounced method on component creation
        this.debouncedSearch = _.debounce(this.getInvoices, 3000);
    },
    mounted() {
        this.checkHeaders();
    },
    watch: {
        type() {
            this.localType = this.type;
            this.checkHeaders();
        },
        options: {
            handler() {
                this.getInvoices(true);
            },
            deep: true
        },
        'loading.invoices'(val) {
            this.$emit('update:loading', val);
        }
    },
    methods: {
        /*loadInvoice(id, loading) {
            let token = this.authentication
            if (loading) this.loading.invoice = true
            axios.get('/maintenance/invoice/' + id, token).then(response => {
                this.invoice = response.data
            }).finally(() => {
                if (loading) this.loading.invoice = false
            }).catch(logErr)
        },*/

        /**
         * Load all data
         * @param {*} loading
         */
        getInvoices(loading = true, type = this.type) {
            if (loading) this.loading.invoices = true;

            let {
                page,
                itemsPerPage,
                sortBy,
                sortDesc,
                search,
                filterBy
            } = this.options;

            sortBy = sortBy.length > 0 ? sortBy : ['id'];
            sortDesc = sortDesc.length > 0 ? sortDesc : [true];

            console.log('options: ', this.options);

            let params = new URLSearchParams({
                page: page,
                itemsPerPage: itemsPerPage,
                sortBy: sortBy,
                sortDesc: sortDesc,
                search: search,
                filterBy: filterBy
            });

            axios
                .get(
                    '/v2/maintenance/invoices/paginate/' +
                        type.axios +
                        '?' +
                        params.toString(),
                    {
                        ...this.authentication
                    }
                )
                .then(response => {
                    this.invoices = response.data.data;
                    this.totalInvoices = response.data.total;
                    console.log("from inv table", response);
                })
                .catch(error => {
                    if (axios.isCancel(error)) {
                        console.log('Request canceled:', error.message);
                    } else {
                        // handle other errors
                        console.error('Request failed:', error);
                    }
                })
                .finally(() => {
                    if (loading) this.loading.invoices = false;
                });
        },
        updateSearch(newSearch) {
            this.options.search = newSearch.input;
            this.options.filterBy = newSearch.filterBy;
            this.options.page = 1;
            this.debouncedSearch();
        },
        updateSort(newSort) {
            this.options.sortBy[0] = newSort.sortBy;
            this.options.sortDesc[0] = newSort.sortDesc;
            this.getInvoices(true);
        },
        updateType(newVal) {
            console.log('New Type:', newVal);
            this.localType = newVal;
            this.$emit('update:type', newVal);
            this.getInvoices(true, newVal);
        },
        editItem(item) {
            this.$emit('edit:item', item);
        },
        deleteItemConfirmDialog(item) {
            let message = 'Are you sure you want to delete this invoice?';
            if (confirm(message)) {
                this.deleteItem(item);
            } else {
                console.log('Cancelled deletion for invoice', item);
                this.$store.dispatch('setSnackbar', {
                    text: 'Cancelled deletion for invoice',
                    color: 'info'
                });
            }
        },
        deleteItem(item) {
            axios
                .delete(
                    '/v2/maintenance/invoices/' +
                        this.type.axios +
                        '/' +
                        item.id,
                    this.authentication
                )
                .then(response => {
                    console.log(response);
                    this.getInvoices(true);
                    this.$store.dispatch('setSnackbar', {
                        color: 'success',
                        text: 'Invoice deleted successfully'
                    });
                })
                .catch(this.logErr);
        },

        viewItem(item) {
            this.$emit('view-item', item);
        },
        saveComments() {
            axios
                .put(
                    '/v2/maintenance/invoices/' +
                        this.invoiceType.axios +
                        '/' +
                        this.form.id,
                    {
                        comments: this.form.comments
                    },
                    this.authentication
                )
                .then(response => {
                    console.log(response);
                    this.commentsDialog = false;
                })
                .catch(this.logErr);
        },
        checkHeaders() {
            const headers = this.invoiceHeaders;
            if (!headers.some(header => header.text === 'Expenses')) {
                headers.push({
                    text: 'Expenses',
                    align: 'center',
                    sortable: false,
                    value: 'data-table-expand'
                });
            }
            //Modify the header that matches the text "Equipment" to have the value of invoiceType
            headers.forEach(header => {
                if (header.text === 'Equipment') {
                    header.value =
                        this.type.axios == 'trailers'
                            ? 'model.Trailer'
                            : 'model.Truck';
                }
            });

            return headers;
        },
        downloadInvoiceReport(item) {
            console.log('Item: ', item, 'Headers: ', this.expenseHeaders);
            sheets.generateSheet(
                item.expenses,
                'csv',
                this.expenseHeaders,
                'Invoice'
            );
        },
        downloadExpenseReport(items, type = 'csv') {
            console.log('Raw Items: ', items);
            sheets.generateSheet(items, type, this.expenseHeaders, 'Expenses');
        },
        logErr(err) {
            console.error(err);
            this.$store.dispatch('setSnackbar', {
                color: 'error',
                text: 'An error occurred. ' + err
            });
        },
        formatDatetime(datetime) {
            let parts = datetime.split(' ');
            return parts[0];
        },
        getFormattedField(value) {
            let cad = new Intl.NumberFormat('en-CA', {
                style: 'currency',
                currency: 'CAD',
            });
            return cad.format(value);
        },
    }
};
</script>
